import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeTask,
  createTask,
  editTask,
  fetchDoneTasks,
  fetchTaskDetail,
  fetchTasks,
  fetchTasksByClient,
  fetchWatcherOptions,
  postponeTask,
  reopenTask,
} from "@/api/tasks";
import type {
  DonePeriod,
  TaskCreatePayload,
  TaskEditPayload,
  TaskListQuery,
  TaskPostponePayload,
  TaskScope,
} from "@/api/types";
import type { Client360TaskCompletePayload } from "@/api/client360Tasks";

const LIST_STALE = 30_000;
const DETAIL_STALE = 30_000;

/**
 * F3 §E — cache invalidation. After every mutation:
 *   - tasks (the list)
 *   - homeSummary (myTasks/overdue KPIs + action queue)
 *   - todayMeetings (if any task was linked from a meeting)
 *   - accountOverview/accountActivitySummary(accountId) when known
 */
function invalidateF3(
  qc: ReturnType<typeof useQueryClient>,
  accountId: string | null,
) {
  qc.invalidateQueries({ queryKey: ["tasks"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  qc.invalidateQueries({ queryKey: ["todayMeetings"] });
  if (accountId) {
    qc.invalidateQueries({ queryKey: ["accountOverview", accountId] });
    qc.invalidateQueries({ queryKey: ["accountActivitySummary", accountId] });
  }
}

export function useTaskList(q: TaskListQuery = {}, enabled = true) {
  const key = [
    "tasks",
    q.scope ?? "my",
    q.view ?? "all",
    q.cursor ?? null,
    q.teamFilter ?? null,
    q.byManager ?? null,
    q.q ?? null,
    q.period ?? null,
  ] as const;
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchTasks(q, signal),
    staleTime: LIST_STALE,
    enabled,
  });
}

const DONE_PAGE = 25;

/**
 * DONE-TASKS D4 — «Виконані»: keyset-пагінація (useInfiniteQuery). Ключ під
 * префіксом ["tasks"] → invalidateF3 (reopen/complete) інвалідує всі сторінки.
 */
export function useDoneTasks(
  q: { scope: TaskScope; q: string; period: DonePeriod },
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ["tasks", "done", q.scope, q.q, q.period] as const,
    queryFn: ({ pageParam, signal }) =>
      fetchDoneTasks(
        {
          scope: q.scope,
          q: q.q || undefined,
          period: q.period === "all" ? undefined : q.period,
          cursor: pageParam ?? undefined,
          limit: DONE_PAGE,
        },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    staleTime: LIST_STALE,
    enabled,
  });
}

/** v1.2 §6.4 — «По клієнтах» grouped view. */
export function useTasksByClient(scope: TaskScope = "my", enabled = true) {
  return useQuery({
    queryKey: ["tasks", "by-client", scope],
    queryFn: ({ signal }) => fetchTasksByClient(scope, signal),
    staleTime: LIST_STALE,
    enabled,
  });
}

export function useTaskDetail(id: string | null) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: ({ signal }) => fetchTaskDetail(id!, signal),
    enabled: !!id,
    staleTime: DETAIL_STALE,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskCreatePayload) => createTask(payload),
    onSuccess: (data) => invalidateF3(qc, data.accountId ?? null),
  });
}

export function useEditTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaskEditPayload }) =>
      editTask(id, body),
    onSuccess: (data) => invalidateF3(qc, data.accountId ?? null),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    // v1.2 R1 — body carries the canonical outcome payload (TaskCompleteModal).
    mutationFn: ({ id, body }: { id: string; body?: Client360TaskCompletePayload }) =>
      completeTask(id, body ?? {}),
    onSuccess: (data) => invalidateF3(qc, data.accountId ?? null),
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenTask(id),
    onSuccess: (data) => invalidateF3(qc, data.accountId ?? null),
  });
}

export function usePostponeTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaskPostponePayload }) =>
      postponeTask(id, body),
    onSuccess: (data) => invalidateF3(qc, data.accountId ?? null),
  });
}

/** D-142.W1 (П-10) — typeahead кандидатів у спостерігачі (active-only, server-owned). */
export function useWatcherOptions(q: string, enabled = true) {
  return useQuery({
    queryKey: ["taskWatcherOptions", q],
    queryFn: ({ signal }) => fetchWatcherOptions(q, signal),
    staleTime: 60_000,
    enabled,
  });
}

/**
 * D-142.W1 (П-9) — мутація watchers (PATCH watchersIds, full-replace). Інвалідує
 * і list-, і detail-кеш: після зняття юзер зникає з list/count без stale-хвоста;
 * detail-ключ ["task", id] НЕ покривається invalidateF3 — додаємо явно.
 */
export function useEditTaskWatchers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, watchersIds }: { id: string; watchersIds: string[] }) =>
      editTask(id, { watchersIds }),
    onSuccess: (data) => {
      invalidateF3(qc, data.accountId ?? null);
      qc.invalidateQueries({ queryKey: ["task", data.id] });
    },
  });
}
